import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { WalletService } from '@/app/services/wallet/wallet.service';
import { take, firstValueFrom, Subscription, debounceTime, Subject } from 'rxjs';
import { BriefService } from '@/app/services/brief/brief.service';
import { FileDownload } from '@/app/services/file-download/file-download';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FormField, form } from '@angular/forms/signals';
import { EmptyStateComponent } from '../../atoms/empty-state/empty-state.component';
import { S3uploaderService } from '@/app/services/s3uploader/s3uploader.service';
import { InvoiceService } from '@/app/services/invoice/invoice.service';
import { ImageService } from '@/app/services/image/image.service';
import { WalletTransactionTypeEnum } from '@/app/common/general-types/wallet-transaction.dto';

@Component({
  selector: 'app-wallet-v2',
  imports: [CurrencyPipe, DatePipe, FormField, EmptyStateComponent],
  templateUrl: './wallet-v2.html',
  styleUrl: './wallet-v2.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WalletV2 implements OnInit, OnDestroy {

  // loadingWallet = signal<boolean>(false);
  loadingState = signal<'idle' | 'loading' | 'loaded' | 'error'>('idle');

  transactions = signal<WalletTransaction[] | undefined>(undefined);
  /** Full creator-hire list (not filtered by export UI). */
  allCreatorTransactions = signal<WalletTransaction[]>([]);
  topupTransactions = signal<WalletTransaction[]>([]);
  briefContextById = signal<Record<string, { title: string; campaignName: string }>>({});

  monthlyCreatorPaidSummary = signal<MonthlyCreatorSummary[]>([]);

  activeStatementTab = signal<'all' | 'all-creators' | 'topups'>('all');


  exportStartDate = signal(WalletV2.firstDayOfYearWindowIso());
  exportEndDate = signal(WalletV2.lastDayOfMonthIso());
  paymentStateFilter = signal<'both' | 'reserved' | 'released'>('both');

  /** Rows for the statement table: derived from dates, payment filter, and source list. */
  filteredCreatorTransactions = computed(() => {
    const start = this.exportStartDate();
    const end = this.exportEndDate();
    const state = this.paymentStateFilter();
    return this.allCreatorTransactions().filter(
      transaction =>
        WalletV2.transactionMatchesPaidDateRange(transaction, start, end) &&
        WalletV2.transactionMatchesPaymentStateFilter(transaction, state)
    );
  });

  filteredTopupTransactions = computed(() => {
    const start = this.exportStartDate();
    const end = this.exportEndDate();
    return this.topupTransactions().filter(transaction =>
      WalletV2.transactionMatchesTopupCreatedDateRange(transaction, start, end)
    );
  });

  /** All wallet movements in the date range, including manual adjustments. */
  filteredAllTransactions = computed(() => {
    const start = this.exportStartDate();
    const end = this.exportEndDate();
    const state = this.paymentStateFilter();
    return (this.transactions() ?? [])
      .filter(transaction => Number(transaction?.amount || 0) !== 0)
      .filter(transaction => WalletV2.transactionMatchesPaidDateRange(transaction, start, end))
      .filter(transaction => {
        if (this.isCreatorTransaction(transaction)) {
          return WalletV2.transactionMatchesPaymentStateFilter(transaction, state);
        }
        return true;
      })
      .sort(
        (a, b) =>
          new Date(this.getStatementDate(b) || 0).getTime() -
          new Date(this.getStatementDate(a) || 0).getTime()
      );
  });

  paymentStateFilterLabel = computed(() => {
    switch (this.paymentStateFilter()) {
      case 'both':
        return 'Both';
      case 'reserved':
        return 'Reserved';
      case 'released':
        return 'Released';
    }
  });

  showDownloadOptions = signal<boolean>(false);
  allCreatorsSkeletonRows = [1, 2, 3, 4, 5, 6];
  invoicesDownloading = signal<string[]>([]);
  invoiceErrorsById = signal<Record<string, string>>({});

  exportError = signal<string | undefined>(undefined);

  subscriptionList = new Subscription();

  private searchQuerySubject = new Subject<string>();

  searchQueryModel = signal({ query: '' });
  searchQueryForm = form(this.searchQueryModel);

  public walletService = inject(WalletService);
  private currentUserService = inject(CurrentUserService);
  private briefService = inject(BriefService);
  private fileDownloadService = inject(FileDownload);
  private uploadService = inject(S3uploaderService);
  private invoiceService = inject(InvoiceService);
  private imageService = inject(ImageService);

  constructor() {
    this.subscriptionList.add(
      this.searchQuerySubject.pipe(debounceTime(350)).subscribe(() => {
        this.loadWallet();
      })
    );

    effect(() => {
      const query = this.searchQueryModel().query;
      this.searchQuerySubject.next(query);
    });
  }

  private static toIsoDateLocal(d: Date): string {
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private static firstDayOfMonthIso(reference = new Date()): string {
    return WalletV2.toIsoDateLocal(new Date(reference.getFullYear(), reference.getMonth(), 1));
  }

  /** First day of the month 11 months before `reference` (12-month window through current month). */
  private static firstDayOfYearWindowIso(reference = new Date()): string {
    return WalletV2.toIsoDateLocal(new Date(reference.getFullYear(), reference.getMonth() - 11, 1));
  }

  private static lastDayOfMonthIso(reference = new Date()): string {
    return WalletV2.toIsoDateLocal(new Date(reference.getFullYear(), reference.getMonth() + 1, 0));
  }

  /** Calendar day YYYY-MM-DD from paidDate for range checks (date prefix when present, else local day). */
  private static sortableDayFromPaidDate(paidDate: string): string | null {
    const trimmed = paidDate.trim();
    const datePrefix = /^(\d{4}-\d{2}-\d{2})/.exec(trimmed);
    if (datePrefix) {
      return datePrefix[1];
    }
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }
    return WalletV2.toIsoDateLocal(parsed);
  }

  private static transactionMatchesPaidDateRange(
    transaction: WalletTransaction,
    startIsoDay: string,
    endIsoDay: string
  ): boolean {
    if (!transaction.paidDate && !transaction.createdDate) {
      return false;
    }
    let whichDate = transaction.paidDate || transaction.createdDate;
    if (!whichDate) {
      return false;
    }
    const day = WalletV2.sortableDayFromPaidDate(whichDate);
    if (!day) {
      return false;
    }
    return day >= startIsoDay && day <= endIsoDay;
  }

  private static transactionMatchesTopupCreatedDateRange(
    transaction: WalletTransaction,
    startIsoDay: string,
    endIsoDay: string
  ): boolean {
    const createdValue = transaction.createdDate ?? transaction.created;
    if (!createdValue) {
      return false;
    }

    const createdDate = typeof createdValue === 'number'
      ? new Date(createdValue * 1000)
      : new Date(createdValue);

    if (Number.isNaN(createdDate.getTime())) {
      return false;
    }

    const day = WalletV2.toIsoDateLocal(createdDate);
    return day >= startIsoDay && day <= endIsoDay;
  }

  private static transactionMatchesPaymentStateFilter(
    transaction: WalletTransaction,
    filter: 'both' | 'reserved' | 'released'
  ): boolean {
    if (filter === 'both') {
      return true;
    }
    const reserved = !!transaction.reservedForPurchase;
    return filter === 'reserved' ? reserved : !reserved;
  }

  onExportStartDateInput(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    this.exportStartDate.set(input.value);
  }

  onExportEndDateInput(event: Event): void {
    const input = event.currentTarget as HTMLInputElement;
    this.exportEndDate.set(input.value);
  }

  onPaymentStateFilterSelected(filter: 'both' | 'reserved' | 'released'): void {
    this.paymentStateFilter.set(filter);
  }

  ngOnInit(): void {
    this.loadWallet();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  loadWallet() {
    this.loadingState.set('loading');
    this.walletService.getTransactions(this.currentUserService.focussedBrand.id, this.searchQueryModel().query).pipe(take(1)).subscribe(async (response: any) => {
      this.transactions.set(response);

      await this.enrichTransactionsWithBriefData(this.transactions()!);
      this.prepareStatementData();

      this.loadingState.set('loaded');
    }, error => {
      console.error('error loading wallet: ', error);
      this.loadingState.set('error');
    }, () => {
      this.loadingState.set('loaded');
    });

    this.subscriptionList.add(
      this.walletService.getBalance(this.currentUserService.focussedBrand.id).subscribe({
        next: () => { },
        error: err => {
          console.error('getBalance error: ', err);
        }
      })
    );
  }

  private async enrichTransactionsWithBriefData(transactions: WalletTransaction[]) {
    const uniqueBriefIds = Array.from(
      new Set(
        transactions
          .map(transaction => this.getTransactionBriefId(transaction))
          .filter((id): id is string => !!id && !this.briefContextById()[id])
      )
    );

    if (uniqueBriefIds.length === 0) {
      return;
    }

    const briefResults = await Promise.allSettled(
      uniqueBriefIds.map(briefId => firstValueFrom(this.briefService.getBriefById(briefId)))
    );

    briefResults.forEach((result, index) => {
      const briefId = uniqueBriefIds[index];
      if (result.status === 'fulfilled' && result.value) {
        const brief = result.value as any;
        this.briefContextById.set({
          ...this.briefContextById(), [briefId]: {
            title: brief?.title || '-',
            campaignName: brief?.campaign?.campaignName || brief?.campaignName || '-',
          }
        });
      }
    });
  }

  private prepareStatementData() {
    this.allCreatorTransactions.set(this.transactions()!
      .filter(transaction => this.isCreatorTransaction(transaction))
      .sort((a, b) => new Date(this.getStatementDate(b) || 0).getTime() - new Date(this.getStatementDate(a) || 0).getTime()));

    this.topupTransactions.set(this.transactions()!
      .filter(transaction => this.isTopupTransaction(transaction))
      .sort((a, b) => new Date(this.getStatementDate(b) || 0).getTime() - new Date(this.getStatementDate(a) || 0).getTime()));

    const groupedByMonth: Record<string, WalletTransaction[]> = {};
    this.allCreatorTransactions().forEach(transaction => {
      const statementDate = this.getStatementDate(transaction);
      const monthKey = this.getMonthKeyFromDate(statementDate);
      const currencyCode = this.getCurrencyCode(transaction);
      if (!monthKey || !currencyCode) {
        return;
      }
      const summaryKey = `${monthKey}|${currencyCode}`;
      groupedByMonth[summaryKey] = groupedByMonth[summaryKey] || [];
      groupedByMonth[summaryKey].push(transaction);
    });

    this.monthlyCreatorPaidSummary.set(Object.keys(groupedByMonth)
      .map(summaryKey => {
        const [monthKey, currencyCode] = summaryKey.split('|');
        const monthlyTransactions = groupedByMonth[summaryKey];
        const grossPennies = monthlyTransactions.reduce((sum, transaction) => sum + Math.abs(Number(transaction.amount || 0)), 0);
        const taxPennies = monthlyTransactions.reduce((sum, transaction) => sum + this.getTaxAmountPennies(transaction), 0);
        return {
          monthKey,
          currencyCode,
          transactionCount: monthlyTransactions.length,
          grossPennies,
          taxPennies,
          netPennies: grossPennies - taxPennies,
        };
      })
    );
  }

  private isTopupTransaction(transaction: WalletTransaction): boolean {
    return Number(transaction?.amount || 0) > 0;
  }

  getMonthKeyFromDate(value: string | null): string {
    if (!value) {
      return '';
    }

    const dt = new Date(value);
    if (Number.isNaN(dt.getTime())) {
      return '';
    }

    const year = dt.getUTCFullYear();
    const month = `${dt.getUTCMonth() + 1}`.padStart(2, '0');
    return `${year}-${month}`;
  }

  getCurrencyCode(transaction: WalletTransaction): string {
    return String(transaction?.currencyCode || '').toUpperCase() || 'GBP';
  }

  /** Hire amounts are VAT-inclusive; tax is 20% of the ex-VAT base. */
  getTaxAmountPennies(transaction: WalletTransaction): number {
    if (this.getCurrencyCode(transaction) === 'USD') {
      return 0;
    }
    const gross = this.getAbsoluteAmountPennies(transaction);
    return gross - this.getNetAmountPennies(transaction);
  }

  getAbsoluteAmountPennies(transaction: WalletTransaction): number {
    return Math.abs(Number(transaction?.amount || 0)) / 100;
  }

  getNetAmountPennies(transaction: WalletTransaction): number {
    const gross = this.getAbsoluteAmountPennies(transaction);
    if (this.getCurrencyCode(transaction) === 'USD') {
      return gross;
    }
    return Math.round((gross / 1.2) * 100) / 100;
  }

  private getTransactionBriefId(transaction: WalletTransaction): string | undefined {
    return (
      transaction?.brief?.id ||
      transaction?.briefId ||
      transaction?.ugcBriefId ||
      transaction?.briefApplication?.brief?.id ||
      transaction?.briefApplication?.briefId ||
      transaction?.application?.brief?.id
    );
  }


  private isCreatorTransaction(transaction: WalletTransaction): boolean {
    return (
      transaction?.amount < 0 &&
      !!transaction?.creator
    );
  }

  getTransactionKind(transaction: WalletTransaction): WalletStatementKind {
    if (this.isTopupTransaction(transaction)) {
      return 'topup';
    }
    if (this.isCreatorTransaction(transaction)) {
      return 'hire';
    }
    return 'adjustment';
  }

  getTransactionKindLabel(transaction: WalletTransaction): string {
    switch (this.getTransactionKind(transaction)) {
      case 'topup':
        return Number(transaction?.type) === WalletTransactionTypeEnum.VoucherTopUp
          ? 'Voucher top-up'
          : 'Top-up';
      case 'hire':
        return 'Creator hire';
      case 'adjustment':
        return 'Adjustment';
    }
  }

  getTransactionKindBadgeClass(transaction: WalletTransaction): string {
    switch (this.getTransactionKind(transaction)) {
      case 'topup':
        return 'rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700';
      case 'hire':
        return 'rounded-full border border-indigo-200 bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700';
      case 'adjustment':
        return 'rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700';
    }
  }

  getTransactionDetails(transaction: WalletTransaction): string {
    switch (this.getTransactionKind(transaction)) {
      case 'hire': {
        const parts = [
          this.getCreatorName(transaction),
          this.getBrandName(transaction),
          this.getBriefTitle(transaction),
        ].filter((part) => part && part !== '-');
        return parts.join(' · ') || '-';
      }
      case 'topup':
        return this.getTopupAddedBy(transaction);
      case 'adjustment': {
        const addedBy = this.getTopupAddedBy(transaction);
        return addedBy === '-' ? 'Manual adjustment' : addedBy;
      }
    }
  }

  getSignedAmountPounds(transaction: WalletTransaction): number {
    return Number(transaction?.amount || 0) / 100;
  }

  getStatementDate(transaction: WalletTransaction): string | null {
    return transaction?.paidDate || transaction?.createdDate || null;
  }

  getStatementDateDisplay(transaction: WalletTransaction): Date | null {
    return transaction?.paidDate ? new Date(transaction.paidDate) : transaction?.createdDate ? new Date(transaction.createdDate) : null;
  }

  getStatementStatusLabel(transaction: WalletTransaction): string {
    return transaction?.reservedForPurchase ? 'Reserved' : 'Released';
  }

  getCreatorName(transaction: WalletTransaction): string {
    const creator = transaction?.creator;
    if (!creator) {
      return '-';
    }

    const firstName = String(creator.firstName || '').trim();
    const lastInitial = String(creator.lastName || '').trim().charAt(0);
    return `${firstName} ${lastInitial}`.trim();
  }

  getCreatorPhotoUrl(transaction: WalletTransaction): string {
    return this.imageService.getImageSource(transaction?.creator?.photoURL || '', 'gallery');
  }

  getBrandName(transaction: WalletTransaction): string {
    const fromTransaction = String(transaction?.companyName || '').trim();
    if (fromTransaction) {
      return fromTransaction;
    }

    const fromFocusedBrand = String(this.currentUserService.focussedBrand?.companyName || '').trim();
    return fromFocusedBrand || '-';
  }

  getBriefTitle(transaction: WalletTransaction): string {
    const brief =
      transaction?.brief ||
      transaction?.briefApplication?.brief ||
      transaction?.application?.brief ||
      transaction?.ugcBrief;

    const briefId = this.getTransactionBriefId(transaction);
    return (
      brief?.title ||
      transaction?.briefTitle ||
      (briefId ? this.briefContextById()[briefId]?.title : undefined) ||
      '-'
    );
  }

  getCampaignName(transaction: WalletTransaction): string {
    const brief =
      transaction?.brief ||
      transaction?.briefApplication?.brief ||
      transaction?.application?.brief ||
      transaction?.ugcBrief;

    const briefId = this.getTransactionBriefId(transaction);
    return (
      brief?.campaign?.campaignName ||
      brief?.campaignName ||
      transaction?.campaign?.campaignName ||
      transaction?.campaignName ||
      (briefId ? this.briefContextById()[briefId]?.campaignName : undefined) ||
      '-'
    );
  }

  openTopUpDrawer() {
    this.walletService.openTopUpDrawer();
  }


  getStatementTabClass(tab: 'all' | 'all-creators' | 'topups'): string {
    return this.activeStatementTab() === tab
      ? 'bg-gray-200 text-gray-900 dark:bg-white/10 dark:text-gray-200'
      : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200';
  }

  async downloadFilteredStatementCsv() {
    if (this.activeStatementTab() === 'topups') {
      await this.downloadTopupStatementCsv();
      return;
    }

    if (this.activeStatementTab() === 'all') {
      await this.downloadAllStatementCsv();
      return;
    }

    await this.downloadCreatorPaidStatementCsv();
  }

  async downloadCreatorPaidStatementCsv() {
    this.exportError.set(undefined);

    if (this.exportStartDate() && this.exportEndDate() && this.exportStartDate() > this.exportEndDate()) {
      this.exportError.set('Start date cannot be after end date.');
      return;
    }

    const filteredTransactions = this.filteredCreatorTransactions();

    if (filteredTransactions.length === 0) {
      this.exportError.set('No creator transactions match this date range.');
      return;
    }

    const statementRows = filteredTransactions.map(transaction => ({
      date: this.getStatementDate(transaction),
      creator: this.getCreatorName(transaction),
      campaign: this.getCampaignName(transaction),
      brief: this.getBriefTitle(transaction),
      amount: (Math.abs(Number(transaction.amount || 0)) / 100).toFixed(2),
      tax: this.getTaxAmountPennies(transaction).toFixed(2),
      netpaid: this.getNetAmountPennies(transaction).toFixed(2),
      currency: this.getCurrencyCode(transaction),
      'invoice number': `${this.getTransactionIdValue(transaction)}`,
      status: this.getStatementStatusLabel(transaction),
    }));

    const fromPart = this.exportStartDate() || 'start';
    const toPart = this.exportEndDate() || 'today';
    await this.fileDownloadService.downloadCsv(
      statementRows,
      `SHOUT_Creator_Paid_Statement_${fromPart}_to_${toPart}.csv`
    );
  }

  async downloadTopupStatementCsv() {
    this.exportError.set(undefined);

    if (this.exportStartDate() && this.exportEndDate() && this.exportStartDate() > this.exportEndDate()) {
      this.exportError.set('Start date cannot be after end date.');
      return;
    }

    const filteredTransactions = this.filteredTopupTransactions();

    if (filteredTransactions.length === 0) {
      this.exportError.set('No wallet top-up transactions match this date range.');
      return;
    }

    const statementRows = filteredTransactions.map(transaction => ({
      date: this.formatStatementDate(this.getStatementDate(transaction)),
      creator: this.getTopupAddedBy(transaction),
      currency: this.getCurrencyCode(transaction),
      amount: (Number(transaction.amount || 0) / 100).toFixed(2),
    }));

    const fromPart = this.exportStartDate() || 'start';
    const toPart = this.exportEndDate() || 'today';
    await this.fileDownloadService.downloadCsv(
      statementRows,
      `SHOUT_Wallet_Topups_Statement_${fromPart}_to_${toPart}.csv`
    );
  }

  getTransactionIdValue(transaction: WalletTransaction) {
    return transaction.id.slice(-6);
  }

  downloadFilteredStatementPdf() {
    if (this.activeStatementTab() === 'topups') {
      this.downloadTopupStatementPdf();
      return;
    }

    if (this.activeStatementTab() === 'all') {
      this.downloadAllStatementPdf();
      return;
    }

    this.exportError.set(undefined);
    const transactions = this.filteredCreatorTransactions();
    if (transactions.length === 0) {
      this.exportError.set('No creator transactions match this date range.');
      return;
    }

    const fromPart = this.exportStartDate() || 'start';
    const toPart = this.exportEndDate() || 'today';
    this.generateStatementPdf(
      transactions,
      'Filtered Creator Statement',
      `SHOUT_Creator_Paid_Statement_${fromPart}_to_${toPart}.pdf`
    );
  }

  async downloadAllStatementCsv() {
    this.exportError.set(undefined);

    if (this.exportStartDate() && this.exportEndDate() && this.exportStartDate() > this.exportEndDate()) {
      this.exportError.set('Start date cannot be after end date.');
      return;
    }

    const filteredTransactions = this.filteredAllTransactions();

    if (filteredTransactions.length === 0) {
      this.exportError.set('No wallet transactions match this date range.');
      return;
    }

    const statementRows = filteredTransactions.map(transaction => ({
      date: this.formatStatementDate(this.getStatementDate(transaction)),
      type: this.getTransactionKindLabel(transaction),
      details: this.getTransactionDetails(transaction),
      amount: this.getSignedAmountPounds(transaction).toFixed(2),
      currency: this.getCurrencyCode(transaction),
      status: this.getTransactionKind(transaction) === 'hire'
        ? this.getStatementStatusLabel(transaction)
        : '-',
    }));

    const fromPart = this.exportStartDate() || 'start';
    const toPart = this.exportEndDate() || 'today';
    await this.fileDownloadService.downloadCsv(
      statementRows,
      `SHOUT_Wallet_Statement_${fromPart}_to_${toPart}.csv`
    );
  }

  private downloadAllStatementPdf(): void {
    this.exportError.set(undefined);
    const transactions = this.filteredAllTransactions();
    if (transactions.length === 0) {
      this.exportError.set('No wallet transactions match this date range.');
      return;
    }

    const fromPart = this.exportStartDate() || 'start';
    const toPart = this.exportEndDate() || 'today';
    this.generateAllStatementPdf(
      transactions,
      'Filtered Wallet Statement',
      `SHOUT_Wallet_Statement_${fromPart}_to_${toPart}.pdf`
    );
  }

  private generateAllStatementPdf(transactions: WalletTransaction[], heading: string, fileName: string): void {
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const generatedDate = new Date().toLocaleString('en-GB');

    pdf.setFontSize(14);
    pdf.text(heading, 40, 40);
    pdf.setFontSize(9);
    pdf.setTextColor(90, 90, 90);
    pdf.text(`Generated: ${generatedDate}`, 40, 56);

    autoTable(pdf, {
      startY: 70,
      head: [['Date', 'Type', 'Details', 'Currency', 'Amount', 'Status']],
      body: transactions.map(transaction => {
        const currencyCode = this.getCurrencyCode(transaction);
        return [
          this.formatStatementDate(this.getStatementDate(transaction)),
          this.getTransactionKindLabel(transaction),
          this.getTransactionDetails(transaction),
          currencyCode,
          this.formatCurrencyFromPennies(Number(transaction.amount || 0), currencyCode),
          this.getTransactionKind(transaction) === 'hire'
            ? this.getStatementStatusLabel(transaction)
            : '-',
        ];
      }),
      styles: { fontSize: 8, cellPadding: 5, overflow: 'linebreak' },
      headStyles: { fillColor: [67, 56, 202] },
      margin: { left: 40, right: 40 },
    });

    pdf.save(fileName);
  }

  private downloadTopupStatementPdf(): void {
    this.exportError.set(undefined);
    const transactions = this.filteredTopupTransactions();
    if (transactions.length === 0) {
      this.exportError.set('No wallet top-up transactions match this date range.');
      return;
    }

    const fromPart = this.exportStartDate() || 'start';
    const toPart = this.exportEndDate() || 'today';
    this.generateTopupStatementPdf(
      transactions,
      'Filtered Wallet Top-Ups Statement',
      `SHOUT_Wallet_Topups_Statement_${fromPart}_to_${toPart}.pdf`
    );
  }

  private generateTopupStatementPdf(transactions: WalletTransaction[], heading: string, fileName: string): void {
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const generatedDate = new Date().toLocaleString('en-GB');

    pdf.setFontSize(14);
    pdf.text(heading, 40, 40);
    pdf.setFontSize(9);
    pdf.setTextColor(90, 90, 90);
    pdf.text(`Generated: ${generatedDate}`, 40, 56);

    autoTable(pdf, {
      startY: 70,
      head: [['Date', 'Creator', 'Currency', 'Amount']],
      body: transactions.map(transaction => {
        const currencyCode = this.getCurrencyCode(transaction);
        return [
          this.formatStatementDate(this.getStatementDate(transaction)),
          this.getTopupAddedBy(transaction),
          currencyCode,
          this.formatCurrencyFromPennies(Number(transaction.amount || 0), currencyCode),
        ];
      }),
      styles: { fontSize: 8, cellPadding: 5, overflow: 'linebreak' },
      headStyles: { fillColor: [67, 56, 202] },
      margin: { left: 40, right: 40 },
    });

    pdf.save(fileName);
  }

  private generateStatementPdf(transactions: WalletTransaction[], heading: string, fileName: string): void {
    const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const rows = this.getStatementPdfRows(transactions);
    const generatedDate = new Date().toLocaleString('en-GB');

    pdf.setFontSize(14);
    pdf.text(heading, 40, 40);
    pdf.setFontSize(9);
    pdf.setTextColor(90, 90, 90);
    pdf.text(`Generated: ${generatedDate}`, 40, 56);

    autoTable(pdf, {
      startY: 70,
      head: [['Creator', 'Campaign', 'Brief', 'Invoice #', 'Currency', 'Amount Paid', 'Tax Paid', 'Date', 'Status']],
      body: rows.map(row => [row.creator, row.campaign, row.brief, row.invoiceNumber, row.currency, row.amountPaid, row.taxPaid, row.date, row.status]),
      styles: { fontSize: 8, cellPadding: 5, overflow: 'linebreak' },
      headStyles: { fillColor: [67, 56, 202] },
      columnStyles: {
        1: { cellWidth: 170 },
        2: { cellWidth: 220 },
      },
      margin: { left: 40, right: 40 },
    });

    pdf.save(fileName);
  }

  private getStatementPdfRows(transactions: WalletTransaction[]): StatementPdfRow[] {
    return transactions.map(transaction => {
      const currencyCode = this.getCurrencyCode(transaction);
      return {
        creator: this.getCreatorName(transaction),
        campaign: this.getCampaignName(transaction),
        brief: this.getBriefTitle(transaction),
        invoiceNumber: this.getTransactionIdValue(transaction),
        currency: currencyCode,
        amountPaid: this.formatCurrencyFromPennies(this.getAbsoluteAmountPennies(transaction) * 100, currencyCode),
        taxPaid: this.formatCurrencyFromPennies(this.getTaxAmountPennies(transaction) * 100, currencyCode),
        date: this.formatStatementDate(this.getStatementDate(transaction)),
        status: transaction?.reservedForPurchase ? 'In Escrow' : 'Released',
      };
    });
  }

  formatCurrencyFromPennies(pennies: number, currencyCode: string = 'GBP'): string {
    const amount = Number(pennies || 0) / 100;
    const code = String(currencyCode || 'GBP').toUpperCase();
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }

  formatStatementDate(value: string | null): string {
    if (!value) {
      return '-';
    }

    return new Date(value).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  createInvoice(transaction: WalletTransaction) {
    this.downloadTransactionDocument(transaction, 'Tax invoice unavailable', 'Invoice');
  }

  showTaxColumn(): boolean {
    const accountCurrency = String(
      this.currentUserService.focussedBrand.currencyCode || ''
    ).toUpperCase();
    return accountCurrency !== 'USD';
  }

  getTopupAddedBy(transaction: WalletTransaction): string {
    if (transaction?.user) {
      const firstName = String(transaction.user.firstName || '').trim();
      const lastName = String(transaction.user.lastName || '').trim();
      return `${firstName} ${lastName}`.trim() || '-';
    }
    return '-';
  }

  downloadTopupReceipt(transaction: WalletTransaction) {
    this.downloadTransactionDocument(transaction, 'Receipt unavailable', 'Receipt');
  }

  private setInvoiceError(transactionId: string, message: string): void {
    this.invoiceErrorsById.update(errors => ({ ...errors, [transactionId]: message }));
  }

  private startInvoiceLoading(transactionId: string): void {
    this.invoicesDownloading.update(ids =>
      ids.includes(transactionId) ? ids : [...ids, transactionId]
    );
  }

  private clearInvoiceLoading(transactionId: string): void {
    this.invoicesDownloading.update(ids => ids.filter(id => id !== transactionId));
  }

  private downloadTransactionDocument(
    transaction: WalletTransaction,
    unavailableMessage: string,
    _downloadLabel: 'Invoice' | 'Receipt'
  ) {
    this.setInvoiceError(transaction.id, '');
    this.startInvoiceLoading(transaction.id);

    const directInvoiceUrl = this.getInvoiceUrlFromTransaction(transaction);
    if (directInvoiceUrl) {
      this.uploadService.downloadBlobFromUrl(this.normalizeInvoiceDownloadUrl(directInvoiceUrl));
      this.clearInvoiceLoading(transaction.id);
      return;
    }

    const invoiceIds = this.getInvoiceLookupIds(transaction);
    this.tryDownloadInvoiceById(transaction, invoiceIds, 0, unavailableMessage);
  }

  private getInvoiceUrlFromTransaction(transaction: WalletTransaction): string | null {
    const invoiceUrlCandidate =
      transaction?.invoiceUrl ||
      transaction?.briefApplication?.invoiceUrl ||
      transaction?.application?.invoiceUrl ||
      transaction?.briefApplication?.invoice?.url ||
      transaction?.application?.invoice?.url;

    const invoiceKeyCandidate =
      transaction?.invoiceKey ||
      transaction?.briefApplication?.invoiceKey ||
      transaction?.application?.invoiceKey ||
      transaction?.briefApplication?.invoice?.key ||
      transaction?.application?.invoice?.key;

    const directUrl = String(invoiceUrlCandidate || '').trim();
    if (directUrl) {
      return directUrl;
    }

    return this.buildS3InvoiceUrlFromKey(invoiceKeyCandidate);
  }

  private normalizeInvoiceDownloadUrl(invoiceUrl: string): string {
    const url = String(invoiceUrl || '').trim();
    const hasPutObjectSignature = /[?&]x-id=PutObject/i.test(url);

    if (hasPutObjectSignature && url.includes('.pdf?')) {
      return `${url.split('.pdf?')[0]}.pdf`;
    }

    return url;
  }

  private getInvoiceLookupIds(transaction: WalletTransaction): string[] {
    const explicitCandidates = [
      transaction?.id,
      transaction?.walletTransactionId,
      transaction?.walletTransaction?.id,
      transaction?.transactionId,
      transaction?.briefApplication?.walletTransactionId,
      transaction?.application?.walletTransactionId,
      transaction?.briefApplication?.id,
      transaction?.application?.id,
      transaction?.briefId,
      transaction?.ugcBriefId,
      transaction?.paymentId,
      transaction?.stripePaymentId,
      transaction?.paymentIntentId,
    ];

    return Array.from(
      new Set(
        explicitCandidates
          .map(value => String(value || '').trim())
          .filter(value => value.length > 5)
          .slice(0, 6)
      )
    );
  }

  private tryDownloadInvoiceById(
    transaction: WalletTransaction,
    invoiceIds: string[],
    index: number,
    unavailableMessage: string
  ): void {
    const invoiceId = invoiceIds[index];
    if (!invoiceId) {
      this.setInvoiceError(transaction.id, unavailableMessage);
      this.clearInvoiceLoading(transaction.id);
      return;
    }

    this.subscriptionList.add(
      this.invoiceService.genInvoice(invoiceId).subscribe({
        next: (invoiceRes: unknown) => {
          const invoiceUrl = this.getInvoiceUrlFromResponse(invoiceRes);
          if (invoiceUrl) {
            this.uploadService.downloadBlobFromUrl(this.normalizeInvoiceDownloadUrl(invoiceUrl));
            this.clearInvoiceLoading(transaction.id);
            return;
          }

          if (index < invoiceIds.length - 1) {
            this.tryDownloadInvoiceById(transaction, invoiceIds, index + 1, unavailableMessage);
            return;
          }

          this.setInvoiceError(transaction.id, unavailableMessage);
          this.clearInvoiceLoading(transaction.id);
        },
        error: (error: unknown) => {
          const canTryNext = index < invoiceIds.length - 1;

          // Keep retries bounded by max candidate IDs (currently 6).
          if (canTryNext) {
            this.tryDownloadInvoiceById(transaction, invoiceIds, index + 1, unavailableMessage);
            return;
          }

          this.setInvoiceError(transaction.id, this.getInvoiceErrorMessage(error, unavailableMessage));
          this.clearInvoiceLoading(transaction.id);
        }
      })
    );
  }

  private getInvoiceErrorMessage(error: unknown, defaultMessage: string): string {
    const err = error as { error?: { message?: string }; message?: string };
    const backendMessage = err?.error?.message || err?.message;
    return String(backendMessage || defaultMessage);
  }

  private getInvoiceUrlFromResponse(response: any): string | null {
    if (typeof response === 'string') {
      const rawValue = response.trim();
      if (rawValue.startsWith('http')) {
        return rawValue;
      }
      return this.buildS3InvoiceUrlFromKey(rawValue);
    }

    const candidate = response?.url ||
      response?.invoiceUrl ||
      response?.signedUrl ||
      response?.downloadUrl ||
      response?.fileUrl ||
      response?.data?.url ||
      response?.data?.invoiceUrl ||
      response?.data?.signedUrl ||
      response?.data?.downloadUrl ||
      response?.data?.fileUrl;

    const value = String(candidate || '').trim();
    if (value && value !== 'undefined' && value !== 'null') {
      return value;
    }

    const fileKey = response?.key ||
      response?.fileKey ||
      response?.invoiceKey ||
      response?.data?.key ||
      response?.data?.fileKey ||
      response?.data?.invoiceKey;

    return this.buildS3InvoiceUrlFromKey(fileKey);
  }

  private buildS3InvoiceUrlFromKey(fileKey: string | undefined): string | null {
    const key = String(fileKey || '').trim();
    if (!key) {
      return null;
    }

    return `https://shout-invoices-static.s3.eu-west-1.amazonaws.com/${key}`;
  }
}

type WalletStatementKind = 'topup' | 'hire' | 'adjustment';

interface WalletTransaction {
  id: string;
  amount: number;
  currencyCode: string;
  walletTransactionId?: string;
  transactionId?: string;
  invoiceUrl?: string;
  invoiceKey?: string;
  reservedForPurchase?: boolean;
  createdDate?: string;
  created?: string | number;
  paidDate?: string;
  status?: number;
  type?: number;
  paymentId?: string;
  stripePaymentId?: string;
  paymentIntentId?: string;
  briefTitle?: string;
  campaignName?: string;
  companyName?: string;
  walletTransaction?: any;
  invoice?: any;
  brief?: any;
  briefId?: string;
  ugcBrief?: any;
  ugcBriefId?: string;
  briefApplication?: any;
  application?: any;
  campaign?: any;
  creator?: { firstName?: string; lastName?: string; photoURL?: string };
  user?: { firstName?: string; lastName?: string };
}

interface MonthlyCreatorSummary {
  monthKey: string;
  currencyCode: string;
  transactionCount: number;
  grossPennies: number;
  taxPennies: number;
  netPennies: number;
}

interface StatementPdfRow {
  creator: string;
  campaign: string;
  brief: string;
  invoiceNumber: string;
  currency: string;
  amountPaid: string;
  taxPaid: string;
  date: string;
  status: string;
}